/* Every address this application used to answer on, and where it goes now.
 *
 * IA.md renames four routes. A link a practitioner has already sent to a
 * colleague, or a browser tab left open since last week, must not land on "there
 * is no page at that address" — so each old path resolves to the new one and
 * says so by replacing the history entry rather than adding to it.
 *
 * The table is data rather than a list of <Navigate> elements so that
 * tools/workspace-check.tsx can assert both halves of the guarantee: that every
 * old path maps somewhere, and that everywhere it maps to is a route that
 * renders. */

export interface RouteRedirect {
  /** The retired path, as a react-router pattern. */
  from: string;
  /** The replacement, with :params carried across by name. */
  to: string;
  why: string;
}

export const REDIRECTS: RouteRedirect[] = [
  {
    from: '/analyze',
    to: '/new',
    why: 'Starting a review is not "analysis"; COPY.md retires the word.',
  },
  {
    from: '/report/:reportId',
    to: '/review/:reportId',
    why: 'A review is the thing; a report was the artefact the old page produced.',
  },
  {
    from: '/report',
    to: '/new',
    why: 'The report index never named a document. Starting a review does.',
  },
  {
    from: '/taxonomy',
    to: '/checks',
    why: '"Taxonomy" is the engine\'s word for it, not a reviewer\'s.',
  },
  {
    from: '/reconcile/:documentId',
    to: '/review/:documentId',
    why: 'Reconciliation stops being a gate in front of the findings; step 11 moves it inline.',
  },
  {
    from: '/reconcile',
    to: '/new',
    why: 'Same, with no document to return to.',
  },
];

/**
 * Fills a redirect target from the params matched out of the old path.
 * `/review/:reportId` with `{ reportId: 'piscataway' }` becomes
 * `/review/piscataway`. A param the path did not carry leaves the segment
 * alone, which is caught by the check rather than shipped as a literal ":id".
 */
export function fillTarget(to: string, params: Record<string, string | undefined>): string {
  return to
    .split('/')
    .map((segment) => {
      if (!segment.startsWith(':')) return segment;
      return params[segment.slice(1)] ?? segment;
    })
    .join('/');
}

/**
 * Where a path goes, without a router. The pattern language is the small part
 * of react-router's that this table uses: literal segments and :params.
 * Returns null for a path that was never retired.
 */
export function redirectFor(pathname: string): string | null {
  const parts = pathname.replace(/\/+$/, '').split('/');

  for (const { from, to } of REDIRECTS) {
    const pattern = from.split('/');
    if (pattern.length !== parts.length) continue;

    const params: Record<string, string> = {};
    const matched = pattern.every((segment, i) => {
      if (segment.startsWith(':')) {
        if (!parts[i]) return false;
        params[segment.slice(1)] = parts[i];
        return true;
      }
      return segment === parts[i];
    });

    if (matched) return fillTarget(to, params);
  }
  return null;
}
